import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, Input, Button } from "reactstrap";
import {
    DashboardPageHeader,
    ResponsiveTabs,
    StatusBadge,
    EmptyState,
    CompactStatsRow,
} from "../../Components/Common/DashboardKit";
import useAuth from "../Personal Attendance/useAuth";
import api from "../../api";
import ReportViewerModal from "./ReportViewerModal";
import {
    DEPARTMENTS,
    STATUS_META,
    departmentLabel,
    inDepartment,
    visibleReports,
} from "./reportConfig";

// ---------------------------------------------------------------------------
// KPI / Reports hub.
//
// Round 1 (UI + config): replaces the coming-soon placeholder with a real
// report hub driven entirely by reportConfig.js. Category tabs, search, and
// report cards. Per-report permission gates (reportConfig GATES) decide which
// cards each user sees on top of the route-level dept.kpi gate.
//
// Open behavior by embed type:
//   iframe   — opens a safe in-hub viewer panel (Power BI publish-to-web).
//              (Round 2 fills in the actual <iframe> rendering; Round 1 shows
//              the viewer shell + open-in-new-tab.)
//   newtab   — opens the URL in a new browser tab (auth-gated links).
//   internal — navigates to the in-app route (X-Ray, wired in Round 3).
//
// Pure presentation + config. No business state, no schema, no API in R1.
// ---------------------------------------------------------------------------

// Per-category gradient used for the placeholder when no preview image loads.
const CATEGORY_GRADIENT = {
    finance:   "linear-gradient(135deg, #405189 0%, #4b38b3 100%)",
    sales:     "linear-gradient(135deg, #0ab39c 0%, #299cdb 100%)",
    inventory: "linear-gradient(135deg, #f7b84b 0%, #f06548 100%)",
    orders:    "linear-gradient(135deg, #299cdb 0%, #405189 100%)",
    quality:   "linear-gradient(135deg, #0ab39c 0%, #405189 100%)",
    vendor:    "linear-gradient(135deg, #4b38b3 0%, #f06548 100%)",
    planning:  "linear-gradient(135deg, #6c757d 0%, #405189 100%)",
    planned:   "linear-gradient(135deg, #adb5bd 0%, #6c757d 100%)",
    default:   "linear-gradient(135deg, #405189 0%, #299cdb 100%)",
};

// Preview thumbnail. Tries the report's preview image; on error (or no file)
// falls back to a clean category-colored gradient with the report icon — so a
// card never looks blank.
// Preview thumbnail with three tiers, in priority order:
//   1. An explicit static image (report.preview) if one was provided.
//   2. For embeddable Power BI reports (embed:"iframe"), a LIVE scaled-down,
//      non-interactive iframe of the publish-to-web report — a real preview
//      with no screenshots needed.
//   3. A clean category-colored gradient + icon (everything else, or if the
//      live frame is slow/blocked) — so a card is never blank.
const PREVIEW_H = 132;
// Authoring size the report is rendered at before scaling (typical PBI canvas).
const FRAME_W = 1280;
const FRAME_H = 720;

// Go-live parity (2026-09-24): `url` is the report's embed link fetched from
// the API (publish-to-web links are no longer in reportConfig / the bundle).
const ReportPreview = ({ report, url }) => {
    const gradient = CATEGORY_GRADIENT[report.category] || CATEGORY_GRADIENT.default;

    // Tier 1: explicit static image.
    const explicit = report.preview || null;
    const [imgFailed, setImgFailed] = useState(false);

    // Tier 2: live iframe for embeddable Power BI reports.
    const canLiveFrame = report.embed === "iframe" && !!url;
    const [frameReady, setFrameReady] = useState(false);
    const [frameFailed] = useState(false);

    // Measure the card width so the scaled frame fills it (no corner gap).
    const wrapRef = useRef(null);
    const [boxW, setBoxW] = useState(0);
    useEffect(() => {
        if (!wrapRef.current) return undefined;
        const el = wrapRef.current;
        const update = () => setBoxW(el.clientWidth);
        update();
        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(update);
            ro.observe(el);
        } else {
            window.addEventListener("resize", update);
        }
        return () => {
            if (ro) ro.disconnect();
            else window.removeEventListener("resize", update);
        };
    }, []);

    const wrapStyle = {
        height: PREVIEW_H,
        borderTopLeftRadius: "0.4rem",
        borderTopRightRadius: "0.4rem",
        overflow: "hidden",
        position: "relative",
        background: gradient,
    };

    const useImage = explicit && !imgFailed;
    const useFrame = !useImage && canLiveFrame && !frameFailed;

    // Scale so the frame's WIDTH fills the card; anchor top-center.
    const scale = boxW > 0 ? boxW / FRAME_W : PREVIEW_H / FRAME_H;

    return (
        <div ref={wrapRef} style={wrapStyle}>
            {useImage && (
                <img
                    src={explicit}
                    alt={`${report.title} preview`}
                    loading="lazy"
                    onError={() => setImgFailed(true)}
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
                />
            )}

            {useFrame && (
                <>
                    <div
                        aria-hidden="true"
                        style={{
                            position: "absolute",
                            top: 0,
                            left: "50%",
                            width: FRAME_W,
                            height: FRAME_H,
                            transform: `translateX(-50%) scale(${scale})`,
                            transformOrigin: "top center",
                            pointerEvents: "none",
                        }}
                    >
                        <iframe
                            title={`${report.title} preview`}
                            src={url}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            tabIndex={-1}
                            scrolling="no"
                            onLoad={() => setFrameReady(true)}
                            style={{ width: "100%", height: "100%", border: 0, pointerEvents: "none" }}
                        />
                    </div>
                    {!frameReady && (
                        <div className="d-flex align-items-center justify-content-center h-100">
                            <i className={`${report.icon || "ri-file-chart-line"} text-white`}
                                style={{ fontSize: 40, opacity: 0.92 }} />
                        </div>
                    )}
                    <div style={{ position: "absolute", inset: 0, zIndex: 1 }} />
                </>
            )}

            {!useImage && !useFrame && (
                <div className="d-flex align-items-center justify-content-center h-100">
                    <i className={`${report.icon || "ri-file-chart-line"} text-white`}
                        style={{ fontSize: 40, opacity: 0.92 }} />
                </div>
            )}

            <span className="position-absolute" style={{ top: 8, right: 8, zIndex: 2 }}>
                <StatusBadge status={(STATUS_META[report.status] || STATUS_META.ready).badge}
                    label={(STATUS_META[report.status] || STATUS_META.ready).label} size="sm" />
            </span>
            {report.featured && (
                <span className="position-absolute badge bg-primary" style={{ top: 8, left: 8, zIndex: 2 }}>
                    <i className="ri-star-line me-1" />Featured
                </span>
            )}
        </div>
    );
};

// Highlight occurrences of `term` within `text` (for search results).
const Highlight = ({ text, term }) => {
    const t = (term || "").trim();
    if (!t) return <>{text}</>;
    const safe = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = String(text).split(new RegExp(`(${safe})`, "ig"));
    return (
        <>
            {parts.map((p, i) =>
                p.toLowerCase() === t.toLowerCase()
                    ? <mark key={i} className="px-0 bg-warning-subtle">{p}</mark>
                    : <React.Fragment key={i}>{p}</React.Fragment>
            )}
        </>
    );
};

const ReportCard = ({ report, embedUrl, onOpen, term }) => {
    const meta = STATUS_META[report.status] || STATUS_META.ready;
    const disabled = !meta.openable;

    return (
        <Card className="h-100 card-animate border-0 shadow-sm overflow-hidden">
            <ReportPreview report={report} url={embedUrl} />
            <CardBody className="d-flex flex-column">
                <div className="d-flex align-items-start mb-2">
                    <div
                        className="avatar-xs flex-shrink-0 me-2 d-flex align-items-center justify-content-center rounded bg-light"
                        style={{ width: 32, height: 32 }}
                    >
                        <i className={`${report.icon || "ri-file-chart-line"} fs-16 text-primary`} />
                    </div>
                    <div className="flex-grow-1">
                        <h6 className="fs-15 mb-1" title={report.title}><Highlight text={report.title} term={term} /></h6>
                        <p className="text-muted fs-12 mb-0"><Highlight text={report.description} term={term} /></p>
                        {/* Owner (2026-10-05): the departments a report belongs to, so the same
                            report under two tabs is no surprise. */}
                        {Array.isArray(report.departments) && report.departments.length > 0 && (
                            <div className="mt-1 d-flex flex-wrap gap-1">
                                {report.departments.map((d) => (
                                    <span key={d} className="badge badge-soft-secondary fw-normal" style={{ fontSize: 10.5 }}>
                                        {departmentLabel(d)}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-auto pt-2">
                    {disabled ? (
                        <Button color="light" disabled block>
                            <i className="ri-time-line me-1" />
                            Planned
                        </Button>
                    ) : (
                        <Button color="primary" outline block onClick={() => onOpen(report)}>
                            {report.embed === "newtab" ? (
                                <>
                                    <i className="ri-external-link-line me-1" />
                                    Open in new tab
                                </>
                            ) : report.embed === "internal" ? (
                                <>
                                    <i className="ri-bar-chart-box-line me-1" />
                                    Open report
                                </>
                            ) : (
                                <>
                                    <i className="ri-eye-line me-1" />
                                    Open report
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

const KPI = () => {
    document.title = "Reports & KPI | SSW Technologies Dashboard";

    const auth = useAuth();
    const navigate = useNavigate();
    // Owner (2026-10-05): the tabs are DEPARTMENTS (Accounting, Sales, Planning, ...), the
    // way the department pages are split; a stored tab from the old grouping falls back to All.
    const [activeTab, setActiveTab] = useState(() => {
        try {
            const stored = sessionStorage.getItem("kpi.activeTab") || "all";
            return DEPARTMENTS.some((d) => d.key === stored) ? stored : "all";
        } catch { return "all"; }
    });
    const [search, setSearch] = useState(() => {
        try { return sessionStorage.getItem("kpi.search") || ""; } catch { return ""; }
    });
    const [viewerReport, setViewerReport] = useState(null);

    // Go-live parity (2026-09-24): publish-to-web links no longer ship in the
    // JS bundle. Fetch the links of the reports this user may open (the API
    // applies the same per-report permission gate as reportConfig) before any
    // report iframe — card preview or viewer — is rendered.
    const [embedUrls, setEmbedUrls] = useState({});
    const [embedStatus, setEmbedStatus] = useState("loading");
    useEffect(() => {
        let alive = true;
        api.get("/reports/embed-urls")
            .then((res) => {
                if (!alive) return;
                const data = res?.data ?? {};
                const items = data.items ?? data.Items ?? [];
                const map = {};
                (Array.isArray(items) ? items : []).forEach((it) => {
                    const id = it?.id ?? it?.Id;
                    const url = it?.url ?? it?.Url;
                    if (id && url) map[id] = url;
                });
                setEmbedUrls(map);
                setEmbedStatus("ready");
            })
            .catch(() => { if (alive) setEmbedStatus("error"); });
        return () => { alive = false; };
    }, []);

    // Round 5: lightweight "recently opened" (session-scoped, ids only).
    const RECENT_KEY = "kpi.recentReports";
    const [recentIds, setRecentIds] = useState(() => {
        try {
            const raw = sessionStorage.getItem(RECENT_KEY);
            const arr = raw ? JSON.parse(raw) : [];
            return Array.isArray(arr) ? arr : [];
        } catch { return []; }
    });

    const pushRecent = useCallback((id) => {
        setRecentIds((prev) => {
            const next = [id, ...prev.filter((x) => x !== id)].slice(0, 5);
            try { sessionStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    }, []);

    // Reports this user may see (per-report permission gate).
    const allowed = useMemo(() => visibleReports(auth), [auth]);

    // Resolve recent ids to visible report objects (drops any now-hidden).
    const recentReports = useMemo(() => {
        return recentIds
            .map((id) => allowed.find((r) => r.id === id))
            .filter(Boolean);
    }, [recentIds, allowed]);

    // Tabs: only show categories that actually have at least one visible report
    // (plus "All"). Counts shown on each tab.
    const tabs = useMemo(() => {
        return DEPARTMENTS.filter((c) => {
            if (c.key === "all") return true;
            return allowed.some((r) => inDepartment(r, c.key));
        }).map((c) => ({
            key: c.key,
            label: c.label,
            icon: c.icon,
            count:
                c.key === "all"
                    ? allowed.length
                    : allowed.filter((r) => inDepartment(r, c.key)).length,
        }));
    }, [allowed]);

    // Persist tab + search for the session (quality-of-life).
    useEffect(() => {
        try { sessionStorage.setItem("kpi.activeTab", activeTab); } catch { /* ignore */ }
    }, [activeTab]);
    useEffect(() => {
        try { sessionStorage.setItem("kpi.search", search); } catch { /* ignore */ }
    }, [search]);

    // Filtered list. When a search is active, it spans ALL categories (so you
    // find a report without first knowing its tab); otherwise filter by tab.
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return allowed.filter((r) => {
            const inSearch =
                !q ||
                r.title.toLowerCase().includes(q) ||
                (r.description || "").toLowerCase().includes(q);
            const inTab = q ? true : inDepartment(r, activeTab);
            return inTab && inSearch;
        });
    }, [allowed, activeTab, search]);

    // Summary stats.
    const stats = useMemo(() => {
        const ready = allowed.filter((r) => r.status === "ready").length;
        const planned = allowed.filter((r) => r.status === "planned").length;
        const financeCount = allowed.filter((r) => r.category === "finance").length;
        return [
            { key: "total",   label: "Reports Available", value: allowed.length, icon: "ri-file-chart-line",          tone: "primary" },
            { key: "ready",   label: "Ready Now",         value: ready,          icon: "ri-checkbox-circle-line",     tone: "success" },
            { key: "finance", label: "Finance",           value: financeCount,   icon: "ri-money-dollar-circle-line", tone: "info"    },
            { key: "planned", label: "Planned",           value: planned,        icon: "ri-time-line",                tone: "secondary" },
        ];
    }, [allowed]);

    const handleOpen = useCallback((report) => {
        pushRecent(report.id);
        if (report.embed === "newtab") {
            if (report.url) window.open(report.url, "_blank", "noopener,noreferrer");
            return;
        }
        if (report.embed === "internal") {
            // In-app route (X-Ray). SPA navigation preserves auth state.
            if (report.url) navigate(report.url);
            return;
        }
        // embed === "iframe": open the safe in-hub viewer (Round 2).
        setViewerReport(report);
    }, [navigate, pushRecent]);

    // Owner (2026-10-05): the department pages link straight to one report
    // (/kpi?report=<id>). Once this user's list is known, that report opens the
    // way a click on its card would, and its tab is selected. An id the user may
    // not see, or an unknown one, just shows the hub. Links that must open in a
    // new tab are not opened from a page load (the browser would block the
    // popup); the selected tab puts their card in front instead.
    const [searchParams] = useSearchParams();
    const wantedReport = searchParams.get("report");
    const openedFromLink = useRef(null);
    useEffect(() => {
        if (!wantedReport || openedFromLink.current === wantedReport) return;
        const report = allowed.find((r) => r.id === wantedReport);
        if (!report) return;
        openedFromLink.current = wantedReport;
        const dept = (report.departments || [])[0];
        if (dept) setActiveTab(dept);
        if (report.embed !== "newtab") handleOpen(report);
    }, [wantedReport, allowed, handleOpen]);

    // Owner (2026-10-05): a department page's "Reports & KPI" card opens its own tab
    // (/kpi?tab=planning). The person can still switch tabs afterwards.
    const wantedTab = searchParams.get("tab");
    useEffect(() => {
        if (wantedTab && DEPARTMENTS.some((d) => d.key === wantedTab)) setActiveTab(wantedTab);
    }, [wantedTab]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <DashboardPageHeader
                        icon="ri-bar-chart-grouped-line"
                        eyebrow="Performance"
                        title="Reports & KPI"
                        subtitle="Central hub for Power BI dashboards and management reports"
                        tone="default"
                        breadcrumb={[
                            { label: "Dashboards", to: "/" },
                            { label: "KPI" },
                        ]}
                    />

                    <CompactStatsRow stats={stats} columns={{ xl: 3, md: 6 }} />

                    {/* Access-level indicator. PURE PERMISSION: the badges
                        reflect the KPI permissions the user actually has (or IT
                        Admin), matching exactly which cards are visible. */}
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-2 mt-1">
                        <span className="text-muted fs-12">Your report access:</span>
                        <span className="badge badge-soft-primary">
                            <i className="ri-folder-chart-line me-1" />
                            Standard reports
                        </span>
                        {(auth?.isItAdmin ||
                          (typeof auth?.hasAccess === "function" &&
                           (auth.hasAccess("KPI - Finance Reports") ||
                            auth.hasAccess("KPI - X-Ray Report")))) && (
                            <span className="badge badge-soft-success">
                                <i className="ri-money-dollar-circle-line me-1" />
                                Finance reports
                            </span>
                        )}
                        {(auth?.isItAdmin ||
                          (typeof auth?.hasAccess === "function" &&
                           auth.hasAccess("KPI - Vendor Reports"))) && (
                            <span className="badge badge-soft-info">
                                <i className="ri-team-line me-1" />
                                Vendor / management
                            </span>
                        )}
                    </div>

                    <Card className="mt-1">
                        <CardBody>
                            <Row className="g-3 align-items-center mb-3">
                                <Col md={8}>
                                    <ResponsiveTabs
                                        tabs={tabs}
                                        activeKey={activeTab}
                                        onChange={setActiveTab}
                                        variant="pills"
                                        size="sm"
                                    />
                                </Col>
                                <Col md={4}>
                                    <div className="position-relative">
                                        <Input
                                            type="search"
                                            className="form-control"
                                            placeholder="Search reports..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            {activeTab === "all" && !search && recentReports.length > 0 && (
                                <div className="mb-3">
                                    <h6 className="text-muted fs-12 text-uppercase mb-2">
                                        <i className="ri-history-line me-1" />
                                        Recently opened
                                    </h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {recentReports.map((r) => (
                                            <Button
                                                key={r.id}
                                                size="sm"
                                                color="light"
                                                className="d-inline-flex align-items-center"
                                                onClick={() => handleOpen(r)}
                                            >
                                                <i className={`${r.icon || "ri-file-chart-line"} me-1 text-primary`} />
                                                {r.title}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {search.trim() && filtered.length > 0 && (
                                <div className="text-muted fs-12 mb-2">
                                    <i className="ri-search-line me-1" />
                                    {filtered.length} result{filtered.length === 1 ? "" : "s"} for “{search.trim()}” across all categories
                                </div>
                            )}

                            {filtered.length === 0 ? (
                                <EmptyState
                                    icon="ri-search-line"
                                    title="No reports match"
                                    tone="secondary"
                                />
                            ) : (
                                <Row className="g-3">
                                    {filtered.map((r) => (
                                        <Col key={r.id} xl={3} lg={4} md={6}>
                                            <ReportCard report={r} embedUrl={embedUrls[r.id]} onOpen={handleOpen} term={search} />
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>

            <ReportViewerModal
                report={viewerReport}
                url={viewerReport ? embedUrls[viewerReport.id] : null}
                urlStatus={embedStatus}
                onClose={() => setViewerReport(null)}
            />
        </React.Fragment>
    );
};

export default KPI;
